import {
  InputBase,
  InputBaseProps,
  MenuItem,
  Select as MuiSelect,
  SelectProps,
  inputBaseClasses,
  menuItemClasses,
  selectClasses,
  styled,
} from '@mui/material';

import { COLOR_DARK, COLOR_DISABLED, COLOR_FOCUS } from '~/components/App/theme';

type ICustomInputProps = InputBaseProps;

export const CustomInput = styled(InputBase)<ICustomInputProps>((props) => {
  const { size } = props;

  const spaceSizeStyle =
    size === 'small'
      ? {
        paddingLeft: 14,
        paddingRight: 14,
        paddingTop: 6,
        paddingBottom: 6,
        fontSize: 14,
        lineHeight: '1.125rem',
      }
      : {
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 10.5,
        paddingBottom: 10.5,
      };

  return {
    marginTop: 0,
    position: 'relative',

    [`& .${inputBaseClasses.input}`]: {
      ...spaceSizeStyle,

      boxSizing: 'border-box',
      borderRadius: 6,

      '&:focus-visible': {
        borderRadius: 8,
        outline: `3px solid ${COLOR_FOCUS}`,
      },
    },
  };
});

export const CustomLightInput = styled(CustomInput)((props) => {
  const {
    size,
    theme: {
      palette: { secondary, background },
    },
  } = props;
  return {
    'div[aria-expanded="true"]': {
      color: secondary.main,
    },

    'div[aria-disabled="true"]': {
      background: background.paper,
    },

    [`& .${inputBaseClasses.input}`]: {
      background: size === 'small' ? background.paper : background.default,

      '&:hover': {
        color: secondary.main,
      },
    },
  };
});

export const CustomDarkInput = styled(CustomInput)((props) => {
  const {
    theme: {
      palette: { primary, dark },
    },
  } = props;
  return {
    'div[aria-disabled="true"]': {
      '&:hover': {
        background: primary.dark,
      },
    },

    [`& .${inputBaseClasses.input}`]: {
      color: primary.contrastText,
      background: primary.dark,

      '&:hover': {
        background: dark.dark,
        fontWeight: 500,
      },
    },
  };
});

export const Select = styled(MuiSelect, { shouldForwardProp: (prop) => prop !== 'dark' })<SelectProps & { dark?: boolean }>((props) => {
  const { dark, theme: { palette: { primary } } } = props;
  return {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '6px',
    [`.${selectClasses.icon}`]: {
      top: 'auto',
      right: '16px',
      width: 12,
      height: 7,
      color: dark ? primary.contrastText : COLOR_DARK,
    },
    [`& .${selectClasses.disabled}`]: {
      color: COLOR_DISABLED,
      WebkitTextFillColor: dark ? primary.light : COLOR_DISABLED,
      [`.${selectClasses.icon}`]: {
        color: COLOR_DISABLED,
      },
    },
  };
});

export const Option = styled(MenuItem)((props) => {
  const { theme: { palette: { primary, secondary } } } = props;
  return {
    color: primary.dark,
    margin: '2px 10px',
    borderRadius: '6px',
    '&:first-of-type': {
      marginTop: '10px',
    },
    '&:last-of-type': {
      marginBottom: '10px',
    },
    '&:hover': {
      background: secondary.light,
    },
    [`&.${menuItemClasses.selected}`]: {
      // important is required in order to override menuItemClasses.focusVisible style
      background: `${secondary.main} !important`,
      color: secondary.contrastText,
    },
  }
});

export const CheckboxOption = styled(Option)((props) => {
  const { theme: { palette: { secondary } } } = props;
  return {
    paddingLeft: 0,
    paddingRight: 0,
    [`&.${menuItemClasses.selected}`]: {
      // important is required in order to override menuItemClasses.focusVisible style
      background: `${secondary.light} !important`,
      color: 'inherit',
    },
    '&:hover span span': {
      background: secondary.light,
      boxShadow: `inset 0 0 0 2px ${secondary.main}, inset 0 -2px 0 ${secondary.main}`,
    },
  }
});

export const RadioOption = styled(Option)((props) => {
  const { theme: { palette: { secondary } } } = props;
  return {
    paddingLeft: 0,
    paddingRight: 0,
    [`&.${menuItemClasses.selected}`]: {
      // important is required in order to override menuItemClasses.focusVisible style
      background: `${secondary.light} !important`,
      color: 'inherit',
    },
    '&:hover span span': {
      boxShadow: `inset 0 0 0 2px ${secondary.main}, inset 0 -2px 0 ${secondary.main}`,
    },
  }
});
